import React, { useEffect, useRef } from 'react';
import { getDashboardNavItems, getNavHandler } from '../config/dashboardNav';
import './DashboardSidebar.css';

function DashboardSidebar({
  currentPage,
  onNavigateHome,
  onNavigateNomineeCheck,
  onNavigateNomineeLookup,
  onNavigateVerificationHistory,
  onNavigateFeedback,
  onNavigateAdmin,
  isAdmin,
  isDrawerOpen = false,
  onCloseDrawer,
  onLogout,
  user,
}) {
  const sidebarRef = useRef(null);
  const navItems = getDashboardNavItems(isAdmin);

  const handlers = {
    onNavigateHome,
    onNavigateNomineeCheck,
    onNavigateNomineeLookup,
    onNavigateVerificationHistory,
    onNavigateFeedback,
    onNavigateAdmin,
  };

  const handleNavClick = (item) => {
    if (item.externalHref) {
      window.open(item.externalHref, '_blank', 'noopener,noreferrer');
      if (onCloseDrawer) onCloseDrawer();
      return;
    }
    const handler = getNavHandler(item, handlers);
    if (handler) handler();
    if (onCloseDrawer) onCloseDrawer();
  };

  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && onCloseDrawer) {
        onCloseDrawer();
      }
    };

    document.body.classList.add('sidebar-drawer-open');
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.classList.remove('sidebar-drawer-open');
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDrawerOpen, onCloseDrawer]);

  return (
    <>
      {isDrawerOpen ? (
        <button
          type="button"
          className="dashboard-sidebar-backdrop is-visible"
          aria-label="Close navigation menu"
          onClick={onCloseDrawer}
        />
      ) : null}

      <aside
        ref={sidebarRef}
        className={`dashboard-sidebar ${isDrawerOpen ? 'is-drawer-open' : ''}`}
        aria-label="Dashboard navigation"
        aria-hidden={false}
      >
        <div className="dashboard-sidebar-inner">
          <p className="dashboard-sidebar-label">Navigation</p>
          <nav className="dashboard-sidebar-nav">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.page ? currentPage === item.page : false;

              return (
                <button
                  key={item.id}
                  type="button"
                  className={`dashboard-sidebar-link ${isActive ? 'active' : ''} ${item.id === 'help' ? 'dashboard-sidebar-link--help' : ''}`}
                  onClick={() => handleNavClick(item)}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className="dashboard-sidebar-icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <span className="dashboard-sidebar-text">
                    <strong>{item.label}</strong>
                    <small>{item.description}</small>
                  </span>
                  {isActive ? <span className="dashboard-sidebar-active-dot" aria-hidden="true" /> : null}
                </button>
              );
            })}
          </nav>

          {isDrawerOpen && onLogout ? (
            <div className="dashboard-sidebar-footer">
              {user ? (
                <div className="dashboard-sidebar-user">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="dashboard-sidebar-avatar" />
                  ) : (
                    <span className="dashboard-sidebar-avatar-fallback" aria-hidden="true">
                      {(user.name || 'U').charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className="dashboard-sidebar-user-text">
                    <strong>{user.name}</strong>
                    <span>{user.email}</span>
                  </div>
                </div>
              ) : null}
              <button
                type="button"
                className="dashboard-sidebar-logout"
                onClick={() => {
                  onLogout();
                  if (onCloseDrawer) onCloseDrawer();
                }}
              >
                Logout
              </button>
            </div>
          ) : null}
        </div>
      </aside>
    </>
  );
}

export default DashboardSidebar;
